import React, { useState, useEffect, useMemo } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import { UploadedFiles } from "../../../types/venue";
import { toast as toastify } from "react-toastify";
import FileUpload2 from "./FileUpload2";

interface Props {
  uploadedFiles: Pick<UploadedFiles, "venue" | "floorPlan">;
  handleFilesChange: (category: keyof UploadedFiles) => (files: File[]) => void;
  existingFiles?: {
    venue: string[];
    floorPlan: string[];
  };
  onExistingFilesChange?: (
    category: "venue" | "floorPlan",
    remainingImages: string[],
  ) => void;
}

// ── styled ────────────────────────────────────────────────────────────────

const SectionLabel = styled(Typography)({
  fontSize: "13px",
  fontWeight: 500,
  color: "#374151",
  marginBottom: "8px",
});

// ── component ─────────────────────────────────────────────────────────────

const VenuePhotosAdd: React.FC<Props> = ({
  uploadedFiles,
  handleFilesChange,
  existingFiles = { venue: [], floorPlan: [] },
  onExistingFilesChange,
}) => {
  const [remainingImages, setRemainingImages] = useState(existingFiles);

  useEffect(() => {
    setRemainingImages(existingFiles);
  }, [existingFiles]);

  // ── helpers ──────────────────────────────────────────────────────────

  const handleExistingDelete = (
    category: "venue" | "floorPlan",
    index: number,
  ) => {
    const updated = remainingImages[category].filter((_, i) => i !== index);
    setRemainingImages((prev) => ({ ...prev, [category]: updated }));
    onExistingFilesChange?.(category, updated);
  };

  const buildFilesProp = (category: "venue" | "floorPlan") => {
    const existingItems = (remainingImages[category] || []).map((url, idx) => ({
      preview: url,
      name: `existing_${idx}`,
      isExisting: true,
      complete: true,
      sizeKB: 0,
      uploadedKB: 0,
    }));

    const uploadedItems = (uploadedFiles[category] || []).map((file) =>
      Object.assign(file, {
        preview:
          (file as File & { preview?: string }).preview ||
          URL.createObjectURL(file),
        complete: true,
        sizeKB: file.size / 1024,
        uploadedKB: file.size / 1024,
      }),
    );

    return [...existingItems, ...uploadedItems];
  };

  const venueFilesProp = useMemo(
    () => buildFilesProp("venue"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remainingImages.venue, uploadedFiles.venue],
  );
  const floorPlanFilesProp = useMemo(
    () => buildFilesProp("floorPlan"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remainingImages.floorPlan, uploadedFiles.floorPlan],
  );

  const handleUploadAppend =
    (category: "venue" | "floorPlan") => (newFiles: File[]) => {
      const currentFiles = uploadedFiles[category] || [];

      const filesWithPreviews = newFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
          complete: true,
          sizeKB: file.size / 1024,
          uploadedKB: file.size / 1024,
        }),
      );

      handleFilesChange(category)([...currentFiles, ...filesWithPreviews]);
    };

  const handleDelete = (category: "venue" | "floorPlan") => (name: string) => {
    if (name.startsWith("existing_")) {
      const idx = parseInt(name.split("_")[1], 10);
      handleExistingDelete(category, idx);
      return;
    }

    const remaining = (uploadedFiles[category] || []).filter(
      (f) => f.name !== name,
    );
    handleFilesChange(category)(remaining);
  };

  // ── render ────────────────────────────────────────────────────────────

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Venue Photos */}
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionLabel>Venue Photos</SectionLabel>
          <FileUpload2
            files={venueFilesProp}
            onChange={handleUploadAppend("venue")}
            onDelete={handleDelete("venue")}
            acceptedFileTypes="image/png,image/jpeg"
            toast={{ error: (msg: string) => toastify.error(msg) }}
            maxSizeMB={10}
          />
        </Grid>

        {/* Floor Plans */}
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionLabel>Floor Plans</SectionLabel>
          <FileUpload2
            files={floorPlanFilesProp}
            onChange={handleUploadAppend("floorPlan")}
            onDelete={handleDelete("floorPlan")}
            acceptedFileTypes="image/png,image/jpeg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            toast={{ error: (msg: string) => toastify.error(msg) }}
            maxSizeMB={30}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default VenuePhotosAdd;
